import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Coupon, useCheckout } from "../../context/CheckoutContext";
import styles from "../../pages/Carrinho/Carrinho.module.css";
import CartProductCard from "../carProductCard/CartProductCard";
import { useAppLocalStorage } from "../../hook/localStorage";
import ApiService from "../../service/ApiService";
import General from "../../helpers/General";
import parse from "html-react-parser";
import { toast } from "react-toastify";
import { ecommerceEvents } from "../../utils/gtm";

const CartCard: FunctionComponent = ({
  text,
  path,
  price,
  onSelect,
  selected,
  index,
  img,
  showProducts,
  show = { discount: false, coupon: false },
  couponURL,
}: {
  showProducts: boolean;
  text: string;
  img: string;
  path: string;
  onSelect: Function;
  selected: boolean;
  index: number;
  price: number;
  show: { discount: boolean; coupon: boolean };
  couponURL?: string;
}) => {
  const {
    cart,
    setTotalPrice,
    coupon,
    setCoupon,
    setPaytimeInfo,
    totalPrice,
    clientAndAffiliate,
    planName,
  } = useCheckout();
  const [firstLoad, setFirstLoad] = useState(true);
  if (firstLoad) {
    if (coupon.message !== "") {
      setCoupon({ ...coupon, message: ""});
    }
  }
  const [total, setTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [showDiscount, setShowDiscount] = useState(false);
  const [generalInfo, setGeneralInfo] = useState({
    terms: "",
    policy: "",
    limitDate: "",
    store_id: "",
    image_disclaimer_text: "",
    valid_prazo_text: "",
  });
  const navigate = useNavigate();
  const apiService = new ApiService();
  const params = useParams();
  const location = useLocation();
  const customIdToast = "coupon-message";

  const setSubtotalValue = useCallback(() => {
    let somaTotal = 0;
    cart.map((prods) => {
      somaTotal = somaTotal + prods.price * prods.quantity;
    });
    setSubTotal(parseFloat(somaTotal.toFixed(2)));
    if (!coupon.found) setTotalPrice(parseFloat(somaTotal.toFixed(2)));
    else setValueWithOrWithoutCupon(somaTotal)
  }, [cart])

  const setValueWithOrWithoutCupon = (value) => {
    const couponValidation =
      General.couponValidation(
        coupon.found, value, clientAndAffiliate?.affiliate_id
      );

      if(couponValidation.couponUnavailable
        || couponValidation.inactiveCoupon
        || couponValidation.expiredCoupon
        || couponValidation.nonExistentCoupon
        || couponValidation.purchaseBelowTheMinimumValue
        || couponValidation.totalValueGreaterThanTheMaximumDiscountValue
      ) {
        setShowDiscount(false);
        if (toast.isActive(customIdToast)) {
          toast.update(customIdToast, {
            render: coupon.message,
            type: 'error',
          });
        } else {
          toast.error(coupon.message, {
            toastId: customIdToast,
          });
        }
      }

    if (couponValidation.validatedCoupon) {
      setShowDiscount(true);
      const message = 'Cupom de desconto aplicado.';
      if (toast.isActive(customIdToast)) {
        toast.update(customIdToast, {
          render: message,
          type: 'success',
        });
      } else {
        toast.success(message, {
          toastId: customIdToast,
        });
      }
      const newTotal = coupon.found?.discount_type === 0
        ? Number(
            General.formatCurrency(
              parseFloat(value) *
              (1 - (parseInt(coupon.found.discount_value) / 100))
            ).replace(/,/g, '')
          )
        : Number(
            General.formatCurrency(
              parseFloat(value) -
              parseInt(coupon.found.discount_value)
            ).replace(/,/g, '')
          );
      setTotalPrice(newTotal);
      setTotal(newTotal);
    }
    if (cart.length === 0) {
      setTotal(0);
    }
  }

  useEffect(() => {
    setSubtotalValue()
  }, [cart, setSubtotalValue]);

  useEffect(() => {
    let somaTotal = 0;
    cart.map((prods) => {
      somaTotal = somaTotal + prods.price * prods.quantity;
    });
    if (coupon.found?.discount_value) setValueWithOrWithoutCupon(somaTotal);
    else setTotalPrice(somaTotal)
  }, [coupon])

  useEffect(() => {
    apiService
      .getTermsAndPolicy()
      .then(
        ({
          terms,
          policy,
          limitDate,
          store_id,
          valid_prazo_text,
          image_disclaimer_text,
        }) => {
          setGeneralInfo({
            terms,
            policy,
            limitDate,
            store_id,
            valid_prazo_text,
            image_disclaimer_text,
          });
          setPaytimeInfo({ store_id });
        }
      );

    if (window.location.pathname === "/checkout-pagamento")
      setCoupon({ ...coupon, show: true });
  }, []);
  const { app } = useAppLocalStorage();

  const findCoupon = (name: string) => {
    apiService.getCoupon(name).then((response: Coupon) => {
      setFirstLoad(false)
      const couponValidation = General.couponValidation(
        response, subTotal, clientAndAffiliate.affiliate_id || app?.id
      );
      let message = '';
      if (
        couponValidation.couponUnavailable
        || couponValidation.couponLimitReached
        || couponValidation.nonExistentCoupon
        || couponValidation.inactiveCoupon
        || couponValidation.expiredCoupon
      ) {
        message = 'Este cupom não pertence a esta URL ou não está mais válido.';
        setCoupon({ ...coupon, name, message });
      }
      if (
        !couponValidation.couponUnavailable
        && !couponValidation.couponLimitReached
        && !couponValidation.nonExistentCoupon
        && !couponValidation.inactiveCoupon
        && !couponValidation.expiredCoupon
        && (
          couponValidation.purchaseBelowTheMinimumValue
          || couponValidation.totalValueGreaterThanTheMaximumDiscountValue
        )
      ) {
        const { min_purchase_value, discount_type, discount_value } = response;
        const value =
          discount_type === 1
            ? parseFloat(min_purchase_value) > parseFloat(discount_value)
              ? min_purchase_value
              : discount_value
            : min_purchase_value;
        message =
          `Cupom inserido, mas não validado. Para utilizá-lo, selecione produtos
          que somados tenham um valor acima de R$
          ${parseFloat(value).toFixed(2).replace('.', ',')}`;
        setCoupon({ ...coupon, name, message });
      }
      if (couponValidation.validatedCoupon) {
        setCoupon({ ...coupon, found: response, message: "" });
      }
    });
  };

  function formatNumber(num: number) {
    let formattedNum = num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Replace commas in the integer part with periods
    let parts = formattedNum.split(".");
    parts[0] = parts[0].replace(/,/g, ".");

    // Join the parts back together
    formattedNum = parts.join(",");

    return formattedNum;
  }

  useEffect(() => {
    if(couponURL && app) {
      findCoupon(couponURL);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponURL, subTotal, app])

  const showMessage =
    General.couponValidation(
      coupon.found, subTotal, clientAndAffiliate?.affiliate_id
    ).validatedCoupon;


    const handleContinueClick = async () => {
      // Then navigate as before
      params.slug 
        ? navigate(`/checkout-dados/${params.slug}${couponURL ? `?cupom=${couponURL}` : ''}`)
        : navigate(`/checkout-dados${couponURL ? `?cupom=${couponURL}` : ''}`);
    };

  return (
    <div className={styles.resumoCarrinho}>
      <div className={styles.carrinho1}>
        <div
          className={styles.titulo}
          style={{
            backgroundColor: app?.primaryColor,
            color: app?.secondaryColor,
          }}
        >
          <div className={styles.continuarACompra}>Carrinho</div>
        </div>
        <div className={styles.resumoCompra}>
          {showProducts
            ? cart.map((prod) => (
                <CartProductCard
                  key={prod.id}
                  id={prod.id}
                  title={prod.name}
                  price={prod.price}
                  img={prod.img}
                  quantity={prod.quantity}
                  description={prod.description}
                />
              ))
            : null}
          <div className={styles.subInfo}>
          <div className={styles.subtotal}>
              <div className={styles.continuarACompra}>Plano</div>
              <div className={styles.vistaPorR}>{`${planName}`}</div>
            </div>
            <div className={styles.subtotal}>
              <div className={styles.continuarACompra}>Subtotal</div>
              <div className={styles.vistaPorR}>{`R$${formatNumber(
                subTotal
              )}`}</div>
            </div>
            <div className={styles.subtotal}>
              <div className={styles.continuarACompra}>Frete</div>
              <div className={styles.grtis}>Grátis</div>
            </div>
          </div>
          <img className={styles.separadorIcon} alt="" src="/vector-1.svg" />
          <div className={styles.subtotal}>
            <div className={styles.cupomText}>Cupom</div>
            <input
              className={`${styles.cupomInput} ${
                coupon.message && !showMessage ? styles.cupomError : ""
              }`}
              value={coupon.name}
              onChange={(e) => {
                setCoupon({ ...coupon, name: e.target.value })
              }}
            />
            <button
              onClick={() => findCoupon(coupon.name)}
              className={styles.cupomBtn}
              id="applyCoupon"
            >
              Aplicar
            </button>
          </div>
          {coupon.message !== "" && !showMessage && (
            <div className={styles.cupomMessage}>{coupon.message}</div>
          )}
          <img className={styles.separadorIcon} alt="" src="/vector-1.svg" />
          {showDiscount && (
            <div className={styles.subtotal}>
              <div className={styles.continuarACompra}>Desconto aplicado</div>
              <div className={styles.vistaPorR}>
                {coupon.found?.discount_type === 0
                  ? `${formatNumber(parseFloat(coupon.found?.discount_value))}%`
                  : `R$${formatNumber(
                      parseFloat(coupon.found?.discount_value as string)
                    )}`}
              </div>
            </div>
          )}
          <div className={styles.total}>
            <div className={styles.vistaPorR}>Total</div>
            <div className={styles.r0000Parent}>
              <div className={styles.r00001}>{`R$${
                coupon.found ? formatNumber(total) : formatNumber(subTotal)
              }`}</div>
              <div className={styles.ouEmAt}>
                {`ou em até 12x de R$${formatNumber(
                  (coupon.found ? total : subTotal) / 12
                )}`}
              </div>
            </div>
          </div>
          {location.pathname.match(/[/&]checkout-([^&]*)/) ? null : (
            <div
              className={subTotal ? styles.filledCart : styles.cta}
              onClick={handleContinueClick}
            >
              <div className={styles.vistaPorR}>Continuar a compra</div>
              <img className={styles.icones} alt="" src="/icones41.svg" />
            </div>
          )}
        </div>
      </div>
      <div className={styles.infoPrazo}>
        <img className={styles.icones} alt="" src="/icones7.svg" />
        <div className={styles.sudesteSulEContainer}>
          <p className={styles.emAt12x}>
            <span>{parse(generalInfo.limitDate)}</span>
          </p>
        </div>
      </div>
      <div className={styles.infoFrete}>
        <img className={styles.icones} alt="" src="/icones42.svg" />
        <div className={styles.sudesteSulEContainer}>
          Frete grátis para todo o Brasil
        </div>
      </div>
      <div className={styles.prazoVlidoAContainer}>
        <p className={styles.emAt12x}>
          {
            generalInfo.valid_prazo_text
          }
          {/* <br/><br/>
          Ao continuar você aceita os Termos e Condições */}
        </p>
        {showProducts ? (
          <>
            <p className={styles.emAt12x}>&nbsp;</p>
            <p className={styles.aoContinuarVocAceitaOsTe}>
              <span
                className={styles.aoContinuarVoc}
              >{`Ao continuar você aceita os `}</span>
              <a
                className={styles.taxasYelly}
                href={generalInfo.terms}
                type="application/pdf"
                target="_blank"
                rel="noreferrer"
              >
                <span>Termos e Condições de Uso</span>
              </a>
            </p>
            <p className={styles.aoContinuarVocConcordaCom}>
              <span
                className={styles.aoContinuarVoc}
              >{`Ao continuar você concorda com a `}</span>
              <a
                className={styles.taxasYelly}
                href={generalInfo.policy}
                type="application/pdf"
                target="_blank"
                rel="noreferrer"
              >
                <span>Política de Privacidade</span>
              </a>
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CartCard;
